import Card from "@mui/material/Card/Card";
import {
    alpha,
    Box,
    Button,
    CardActionArea,
    CardActions,
    CardContent,
    CardMedia,
    Divider,
    Grid,
    Tooltip,
    Typography,
} from "@mui/material";
import * as React from "react";
import {FC, memo, MouseEvent, useMemo, useState} from "react";
import {IWish, WishType} from "../../../../../store/wish/Helper";
import {useTranslation} from "react-i18next";
import TimeUntilStartGrid from "../../common/time-until-start-grid/TimeUntilStartGrid";
import {FavoriteBorder, FavoriteOutlined} from "@mui/icons-material";
import {RootState, useAppDispatch, useAppSelector} from "../../../../../store";
import TicketsCountLinearProgress from "../../../../shared/tickets-count-linear-progress/TicketsCountLinearProgress";
import {addToWishlist, removeFromWishlist} from "../../../../../store/wishlist/ActionCreators";
import MakeAWishGrid from "../../common/make-a-wish-grid/MakeAWishGrid";
import {formatPrice, getTextColorOnRecommended} from "../../../../../util/utils";
import ReactGA from "react-ga4";
import CompanyLabel from "../../../../shared/company-label/CompanyLabel";
import {Link as RouterLink} from "react-router-dom";
import {RoutePaths} from "../../../../../util/constants";
import {Swiper as SwiperType} from "swiper";
import GiftBow from "../../../../shared/gift-bow/GiftBow";
import useWishCountdown from "../../../../../util/hooks/useWishCountdown";
import CardWinnerPicker from "../../../../shared/card-winner-picker/CardWinnerPicker";
import Confetti from "react-confetti";
import genie_points from "../../../../../assets/images/genie-points-short-black.svg";
import DiscountRibbon from "../../../../shared/discount-ribbon/DiscountRibbon";
import WinnersBadge from "../../../../shared/winners-badge/WinnersBadge";
import {metaTrack} from "../../../../../util/metaPixel";

interface Props {
    wish: IWish;
    handleOpenWishDetails: (wishId: string) => void;
    swiperInstance?: SwiperType;
}

const WishGridItem: FC<Props> = (props) => {
    const {t, i18n} = useTranslation();
    const isLoggedIn = useAppSelector((s: RootState) => !!s.authReducer.loggedInUser.id);
    const isAdmin = useAppSelector((s: RootState) => s.authReducer.loggedInUser.is_admin);
    const isClient = useAppSelector((s: RootState) => !!s.authReducer.loggedInUser.client_id);
    const wishlist = useAppSelector((s: RootState) => s.wishlistReducer.items);

    const [qty, setQty] = useState(1);
    const dispatch = useAppDispatch();

    const {countDown, drawStarted, ticketsArray, winners} = useWishCountdown(
        props.wish
    );

    const isFavourite = useMemo(
        () => wishlist.some((w) => w.id === props.wish.id),
        [wishlist, props.wish.id]
    );

    const isRecommended = useMemo(
        () => props.wish.recommended || props.wish.type === WishType.DRAW,
        [props.wish.recommended, props.wish.type]
    );

    const getColor = useMemo(() => getTextColorOnRecommended(isRecommended), [isRecommended]);
    const getButtonColor = useMemo(() => (
        isRecommended ? 'tertiary' : 'primary'
    ), [isRecommended])

    const isJpgThumbnail =
        props.wish.thumbnail.toLowerCase().endsWith(".jpg") ||
        props.wish.thumbnail.toLowerCase().endsWith(".jpeg");

    const showFavoritesButton = isLoggedIn && !isAdmin && !isClient;
    const options = useMemo(() => [props.wish, ...(props.wish.children ?? [])], [props.wish]);

    const handleOnFavouriteClick = (e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (isFavourite) {
            dispatch(removeFromWishlist(props.wish));
            return;
        }
        ReactGA.event("add_to_wishlist", {
            currency: "MKD",
            value: props.wish.cost,
            items: [{item_id: props.wish.id, item_name: props.wish[`name_${i18n.language}`]}],
        });
        void dispatch(addToWishlist(props.wish))
            .unwrap()
            .then(() => {
                metaTrack("AddToWishlist", {
                    content_ids: [props.wish.id],
                    content_name: props.wish.name_en,
                    content_type: props.wish.type,
                    currency: "MKD",
                    value: props.wish.cost ?? 0,
                });
            })
            .catch(() => undefined);
    };

    const hasWinners = (props.wish.winners?.length ?? 0) > 0 || winners.length > 0;

    return (
        <Card
            elevation={3}
            sx={{
                display: "flex",
                flexDirection: "column",
                width: {xs: "90%", sm: 265},
                transition: "transform 0.15s ease-in-out",
                "&:hover": {transform: "scale3d(1.05, 1.05, 1)"},
                backgroundColor: "transparent",
                border: `1px solid ${getColor}`,
                my: 2,
                mx: 0.8,
                position: "relative",
                justifyContent: "space-between",
                boxShadow: 3,
            }}
        >
            {/* Top overlay actions (exact sizes/colors as before) */}
            <Box
                position="absolute"
                sx={{
                    width: "calc(100% - 0.8em)",
                    top: "0.4em",
                    ml: "0.4em",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    gap: 0.5,
                    zIndex: 1,
                }}
            >
                {showFavoritesButton && (
                    <Button
                        variant="contained"
                        color={isRecommended ? "tertiary" : "primary"}
                        size="small"
                        onClick={handleOnFavouriteClick}
                        endIcon={isFavourite ? <FavoriteOutlined/> : <FavoriteBorder/>}
                        sx={{
                            height: 31,
                            width: 31,
                            minWidth: "fit-content",
                            fontWeight: 600,
                            p: 0,
                            "& > span": {m: 0},
                            boxShadow: 3,
                            borderRadius: "50%", // ensure circular pill
                        }}
                    />
                )}
                {props.wish.type === WishType.VOUCHER && (
                    <Tooltip title={t("points_reward_tooltip")} arrow enterTouchDelay={0}>
                        <Button
                            variant="contained"
                            size="small"
                            color={isRecommended ? "tertiary" : "primary"}
                            endIcon={<img src={genie_points} alt="Genie points" height={18}/>}
                            sx={{
                                minWidth: "fit-content",
                                fontWeight: 600,
                                "& > span": {ml: "2px"},
                                boxShadow: 3,
                                height: 31,
                            }}
                        >
                            {props.wish.points_award}{props.wish.children?.length ? "+" : ""}
                        </Button>
                    </Tooltip>
                )}
            </Box>

            {/* Ribbons */}
            <GiftBow showRibbon={props.wish.type === WishType.DRAW}/>
            <DiscountRibbon cost={props.wish.cost} originalCost={props.wish.original_cost} type={props.wish.type}/>

            <CardActionArea
                sx={{flex: 1, display: "flex", flexDirection: "column", position: "relative"}}
                onClick={() => props.handleOpenWishDetails(props.wish.id)}
                component={RouterLink}
                to={`${RoutePaths.WISHES}/${props.wish.id}`}
            >
                {winners?.length > 0 && <Confetti gravity={0.05}/>}
                <Box sx={{position: "relative", width: "100%"}}>
                    <CardMedia
                        component="img"
                        sx={{
                            objectFit: isJpgThumbnail ? "cover" : "contain",
                            height: "auto",
                            width: "100%",
                            aspectRatio: "16/9",
                            borderBottom: !isJpgThumbnail && props.wish.type === WishType.DRAW ? `1px solid ${alpha(getColor, 0.22)}` : "none",
                        }}
                        image={props.wish.thumbnail}
                        alt={props.wish[`name_${i18n.language}`]}
                    />

                    {props.wish.type === WishType.DRAW && (
                        <WinnersBadge
                            count={props.wish.number_of_winners}
                            color={getColor}
                            sx={{
                                position: "absolute",
                                left: 0,
                                bottom: "0.4em",
                                zIndex: 2,
                                maxWidth: "calc(100% - 0.8em)",
                            }}
                        />
                    )}
                </Box>

                <CardContent
                    sx={{
                        p: 1,
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        gap: "1em",
                        width: "100%",
                    }}
                >
                    {/* Title + company */}
                    <Box>
                        <Typography
                            textAlign="center"
                            variant="h5"
                            sx={{color: getColor, fontSize: "1.2rem"}}
                            fontWeight={600}
                        >
                            {props.wish[`name_${i18n.language}`]}
                        </Typography>
                        <Grid container justifyContent="center">
                            <CompanyLabel
                                company={props.wish.company}
                                companyUrl={props.wish.company_url}
                                color={getColor}
                            />
                        </Grid>

                        {/* Price block with exact spacing + dividers */}
                        <Box display="flex" flexDirection="column" gap={0.5} mt={2}>
                            <Divider sx={{backgroundColor: alpha(getColor, 0.6)}}/>

                            {props.wish.cost !== 0 ? (
                                <Grid container justifyContent="space-between" alignItems="center" minHeight="2.3em">
                                    <Typography variant="subtitle1" sx={{color: getColor}}>
                                        {t("cost")}:
                                    </Typography>

                                    <Box>
                                        <Grid container gap={0.5} alignItems="center">
                                            {!!props.wish.children?.length && (
                                                <Typography variant="body2" sx={{color: getColor}}>
                                                    {t("from")}
                                                </Typography>
                                            )}

                                            <Box>
                                                {props.wish.original_cost && (
                                                    <Typography
                                                        variant="subtitle1"
                                                        sx={{
                                                            color: alpha(getColor, 0.6),
                                                            textDecoration: "line-through",
                                                            lineHeight: 1.1,
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: 0.25,
                                                        }}
                                                        fontWeight={600}
                                                    >
                                                        {formatPrice(props.wish.original_cost)}
                                                    </Typography>
                                                )}

                                                <Typography
                                                    variant="subtitle1"
                                                    sx={{color: getColor}}
                                                    fontWeight={600}
                                                    lineHeight={1.1}
                                                    display="flex"
                                                    gap={0.25}
                                                    alignItems="center"
                                                >
                                                    {formatPrice(props.wish.cost)}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </Box>
                                </Grid>
                            ) : (
                                <Grid container justifyContent="center" alignItems="center" minHeight="2.3em">
                                    <Typography
                                        variant="subtitle1"
                                        sx={{color: getColor}}
                                        fontWeight={600}
                                        lineHeight={1.1}
                                    >
                                        {props.wish.type === WishType.DRAW ? t("free_entry") : t("free_voucher")}
                                    </Typography>
                                </Grid>
                            )}

                            <Divider sx={{backgroundColor: alpha(getColor, 0.6)}}/>
                        </Box>
                    </Box>

                    {/* Progress + timer + winner (unchanged) */}
                    <Box display="flex" flexDirection="column" gap={2}>
                        {!hasWinners && <TicketsCountLinearProgress wish={props.wish} recommended={isRecommended}
                                                                    accumulateTotals/>}

                        <TimeUntilStartGrid
                            countDown={countDown}
                            recommended={isRecommended}
                            drawStarted={drawStarted}
                        />
                    </Box>


                </CardContent>
            </CardActionArea>
            <CardActions sx={{p: 0}}>
                <MakeAWishGrid
                    wish={props.wish}
                    color={getColor}
                    winners={winners}
                    countDown={countDown}
                    drawStarted={drawStarted}
                    qty={qty}
                    setQty={setQty}
                    buttonSize="small"
                    options={options}
                    swiperInstance={props.swiperInstance}
                    accumulateTotals
                />
            </CardActions>
            {(ticketsArray.length > 0 || winners?.length > 0 || (props.wish.winners?.length ?? 0) > 0) && (
                <CardWinnerPicker
                    thumbnail={props.wish.thumbnail}
                    tickets={ticketsArray}
                    winners={winners}
                    color={getColor}
                    wish={props.wish}
                    buttonColor={getButtonColor}
                />
            )}
        </Card>
    );
};

export default memo(WishGridItem);
